import React, { useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import Script from 'next/script';

import { NextLink } from '@/atoms';

import { DEFAULT_TOAST_OPTIONS_WARNING } from '@/components/providers';
import { WORDPRESS_MUTATION } from '@/lib/graphql/enums';
import APClient from '@/lib/graphql/utils';
import { useCart, useCheckout, useToastDispatch } from '@/lib/hooks';
import { routes } from '@/lib/routes';
import {
  gtmPush,
  normalizeProducts,
  normalizeString,
  priceToNumber,
} from '@/lib/utils';
import { config } from '@/config';

export const PayPalButton = ({
  setFieldError,
  error,
  paypalToken,
}: {
  setFieldError: (field: string, message?: string) => void;
  error: any;
  paypalToken: string;
}) => {
  const { cart } = useCart();
  const refDeseo = useRef<HTMLInputElement>(null);
  const [invalid, setInvalid] = useState('');
  const { checkout, setCheckoutData, getWpCheckoutData } = useCheckout();
  const session = useSession();
  const router = useRouter();
  const { toast } = useToastDispatch();

  const onApprove = async (data: any) =>
    fetch(
      `${process.env.NEXT_PUBLIC_FRONT_URL}${process.env.NEXT_PUBLIC_API_ENDPOINT}?wc-ajax=ppc-approve-order`,
      {
        method: 'POST',
        body: JSON.stringify({
          nonce: '9661917d88',
          order_id: data.orderID,
          funding_source: 'paypal',
        }),
        credentials: 'include',
        headers: {
          authorization: `Bearer ${(session as any).accessToken}`,
          'woocommerce-session': `Session ${localStorage.getItem(
            config.session.woocommerce,
          )}`,
        },
      },
    )
      .then((res) => res.json())
      .then(async () => {
        gtmPush({
          event: 'add_payment_info',
          ecommerce: {
            currency: 'EUR',
            value: priceToNumber(cart.total),
            items: normalizeProducts({ cart, itemListName: 'cart' }),
            payment_type: 'paypal',
            im_payment_type: 'paypal',
          },
        });
        const sendData = getWpCheckoutData();

        sendData.metaData = [
          {
            key: 'is_gift',
            value: checkout?.isGift ? 'true' : 'false',
          },
          { key: '_shipping_email', value: checkout.email },
          {
            key: '_create_guest_user',
            value: checkout.createUser ? 'true' : 'false',
          },
        ];

        return await APClient.mutate({
          mutation: WORDPRESS_MUTATION.CHECKOUT,
          variables: sendData,
        })
          .then((r) => {
            router.push(
              `${routes.checkout.gracias}?order=${r.data.checkout.order.databaseId}`,
            );
          })
          .catch(() => {
            toast(
              'Error al crear el pedido, inténtalo de nuevo',
              DEFAULT_TOAST_OPTIONS_WARNING,
            );
            gtmPush({
              event: 'add_payment_info_error',
              eventData: {
                error_type: 'server',
                error_message: normalizeString(
                  'Error al crear el pedido, inténtalo de nuevo',
                ).replaceAll(' ', '_'),
              },
            });
          });
      });

  const createOrder = async () => {
    const unique = {
      nonce: 'a25bfa4ef1',
      payer: {
        email_address: 'sb-f478dl6269557@business.example.com',
        name: {
          surname: 'alvaro',
          given_name: 'garcia',
        },
        address: {
          country_code: 'ES',
          address_line_1: 'estenam nauel billegas',
          address_line_2: 'linea 2',
          admin_area_1: 'LO',
          admin_area_2: 'Logroño',
          postal_code: '50002',
        },
        phone: {
          phone_type: 'HOME',
          phone_number: {
            national_number: '941505050',
          },
        },
      },
      bn_code: 'Woo_PPCP',
      context: 'checkout',
      order_id: '0',
      payment_method: 'ppcp-gateway',
      funding_source: 'paypal',
      form: 'billing_first_name=John&billing_last_name=Doe&billing_company=&billing_country=ES&billing_address_1=Direccion%201&billing_address_2=linea%202&billing_postcode=26001&billing_city=Logro%C3%B1o&billing_state=LO&billing_phone=941505050&billing_email=sb-xpf8a487346%40personal.example.com&billing_agente_navegador=Mozilla%2F5.0%20(X11%3B%20Linux%20x86_64)%20AppleWebKit%2F537.36%20(KHTML%2C%20like%20Gecko)%20Chrome%2F97.0.4692.71%20Safari%2F537.36&billing_idioma_navegador=es-ES&billing_altura_pantalla=1080&billing_anchura_pantalla=1920&billing_profundidad_color=24&billing_diferencia_horaria=-120&shipping_first_name=John&shipping_last_name=Doe&shipping_company=&shipping_country=ES&shipping_address_1=Direccion%201&shipping_address_2=linea%202&shipping_postcode=26001&shipping_city=Logro%C3%B1o&shipping_state=LO&order_comments=&shipping_method%5B0%5D=flat_rate%3A1&payment_method=ppcp-gateway&woocommerce-process-checkout-nonce=198018d041&_wp_http_referer=%2F%3Fwc-ajax%3Dupdate_order_review&ppcp-funding-source=paypal',
      createaccount: false,
    };

    return await fetch(
      `${process.env.NEXT_PUBLIC_FRONT_URL}${process.env.NEXT_PUBLIC_API_ENDPOINT}?wc-ajax=ppc-create-order`,
      {
        method: 'POST',
        body: JSON.stringify(unique),
        credentials: 'include',
        headers: {
          authorization: `Bearer ${(session as any)?.accessToken}`,
          'woocommerce-session': `Session ${localStorage.getItem(
            config.session.woocommerce,
          )}`,
        },
      },
    )
      .then(async (r) => {
        const woocommerceSession = r.headers.get('woocommerce-session');
        const data = await r.json();

        if (woocommerceSession) {
          localStorage.setItem(
            config.session.woocommerce,
            woocommerceSession.replace('Session ', ''),
          );
        }

        return data.data.id;
      })
      .catch(() =>
        toast(
          'Error al crear el pedido, inténtalo de nuevo',
          DEFAULT_TOAST_OPTIONS_WARNING,
        ),
      );
  };

  return (
    <>
      <Script
        src={`https://www.paypal.com/sdk/js?client-id=${process.env.NEXT_PUBLIC_PAYPAL_CLIENT}&currency=EUR&components=messaging,buttons&enable-funding=paylater&intent=capture&commit=true`}
        data-client-token={paypalToken}
        async
        onReady={() => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          window.paypal
            .Buttons({
              onApprove: onApprove,
              createOrder: createOrder,
              fundingSource: 'paypal',
              onError: () => setInvalid('Expected an order id to be passed'),
              onInit: (_: any, actions: any) => {
                actions.disable();
                setFieldError('acepto', 'Acepta la política de privacidad');
                document
                  ?.querySelector('#check_edad')
                  ?.addEventListener('change', function (event) {
                    // Enable or disable the button when it is checked or unchecked

                    if ((event.target as HTMLInputElement)?.checked) {
                      setFieldError('acepto', '');
                      actions.enable();
                    } else {
                      setFieldError(
                        'acepto',
                        'Acepta la política de privacidad',
                      );
                      actions.disable();
                    }
                  });
              },
              style: { shape: 'pill' },
            })
            .render('#paypal-button-container');

          if (
            session.data?.user?.email?.includes('globant') ||
            session.data?.user?.email?.includes('estrellagalicia.es')
          ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            window.paypal
              .Buttons({
                onApprove: onApprove,
                createOrder: createOrder,
                fundingSource: 'paylater',
                onError: () => setInvalid('Expected an order id to be passed'),
                onInit: (_: any, actions: any) => {
                  actions.disable();
                  setFieldError('acepto', 'Acepta la política de privacidad');
                  document
                    ?.querySelector('#check_edad')
                    ?.addEventListener('change', function (event) {
                      // Enable or disable the button when it is checked or unchecked

                      if ((event.target as HTMLInputElement)?.checked) {
                        setFieldError('acepto', '');
                        actions.enable();
                      } else {
                        setFieldError(
                          'acepto',
                          'Acepta la política de privacidad',
                        );
                        actions.disable();
                      }
                    });
                },
                style: { shape: 'pill' },
              })
              .render('#paypal-button-paylater-container');
          }
        }}
      />

      <div className="relative z-0 max-w-[375px]">
        <div className="flex">
          <input id="check_edad" type="checkbox" name="legales" />
          <label
            htmlFor="check_edad"
            className="u-body u-body--xs mb-2 ml-2 block"
          >
            *Soy mayor de 18 años y he leído y acepto la{' '}
            <NextLink href={routes.legalNoticePrivacyPolicy}>
              <a className="text-secondary-400">política de privacidad</a>
            </NextLink>{' '}
            y{' '}
            <NextLink href={routes.legalNoticePrivacyPolicy}>
              <a className="text-secondary-400">los términos y condiciones</a>
            </NextLink>
            .
          </label>
        </div>

        {session.status === 'unauthenticated' && (
          <div className="flex">
            <input
              ref={refDeseo}
              id="check"
              type="checkbox"
              name="usuario"
              onChange={() => {
                setCheckoutData({
                  ...checkout,
                  createUser:
                    (refDeseo?.current && refDeseo.current.checked) || false,
                });
              }}
            />
            <label
              htmlFor="check"
              className="u-body u-body--xs mb-2 ml-2 block"
            >
              Deseo crear un perfil de usuario para recibir comunicaciones y
              envíos personalizados de Hijos de Rivera S.A.U.
            </label>
          </div>
        )}

        <p className="u-label u-label--xs mb-6 text-status-error">{error}</p>
        <p className="u-label u-label--xs mb-6 text-status-error">{invalid}</p>

        <div id="paypal-button-container" />
        <div id="paypal-button-paylater-container" />
      </div>
    </>
  );
};
